import { memo, useMemo, useState } from 'react'
import { ActionIcon, Anchor, Badge, Button, Group, Table, Text } from '@mantine/core'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { TbChevronDown, TbChevronRight, TbMap2 } from 'react-icons/tb'
import type { RouteResponse } from '@/api/main/types.gen'
import { PROBLEM_COLORS } from './problemColors'
import RouteProblemDetails from './RouteProblemDetails'
import { problemTypesOf, visibleProblemTypes, type ProblemType, type RouteProblem } from './routeProblems'

type RouteProblemRowProps = {
  problem: RouteProblem
  route: RouteResponse | undefined
  selectedTypes: ProblemType[]
  canViewRoutes: boolean
  canPlanRoutes: boolean
  columnCount: number
}

// Memoized with its own expanded state, so toggling or filtering does not re-render the other rows
const RouteProblemRow = ({
  problem,
  route,
  selectedTypes,
  canViewRoutes,
  canPlanRoutes,
  columnCount,
}: RouteProblemRowProps) => {
  const { t } = useTranslation('routeProblems')
  const [isExpanded, setIsExpanded] = useState(false)
  const detailTypes = useMemo(() => visibleProblemTypes(problem, selectedTypes), [problem, selectedTypes])

  return (
    <>
      <Table.Tr style={{ cursor: 'pointer' }} onClick={() => setIsExpanded(expanded => !expanded)}>
        <Table.Td>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label={t(isExpanded ? 'hideDetails' : 'showDetails')}
            aria-expanded={isExpanded}
          >
            {isExpanded ? <TbChevronDown size={16} /> : <TbChevronRight size={16} />}
          </ActionIcon>
        </Table.Td>
        <Table.Td>
          <Group gap="xs" wrap="nowrap">
            {canViewRoutes ? (
              <Anchor
                renderRoot={props => (
                  <Link
                    to="/route/$routeId/$version"
                    params={{ routeId: problem.routeId, version: problem.routeVersion }}
                    {...props}
                  />
                )}
                fw={600}
                size="sm"
                onClick={event => event.stopPropagation()}
              >
                {problem.routeId}
              </Anchor>
            ) : (
              <Text fw={600} size="sm">
                {problem.routeId}
              </Text>
            )}
            {!problem.isActive && (
              <Badge size="xs" color="gray" variant="outline">
                {t('inactive')}
              </Badge>
            )}
          </Group>
        </Table.Td>
        <Table.Td>
          <Text size="sm" ff="monospace">
            {problem.routeVersion}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" c={route?.name ? undefined : 'dimmed'}>
            {route?.name ?? '—'}
          </Text>
        </Table.Td>
        <Table.Td>
          <Group gap={6}>
            {problemTypesOf(problem).map(type => (
              <Badge key={type} color={PROBLEM_COLORS[type]} variant="light" size="sm" title={t(`types.${type}.label`)}>
                {t(`types.${type}.short`)} {problem[type].length}
              </Badge>
            ))}
          </Group>
        </Table.Td>
        <Table.Td ta="right">
          {canPlanRoutes && route && (
            <Button
              renderRoot={props => (
                <Link
                  to="/route-planner/$areaId/$routeId/$version"
                  params={{
                    areaId: route.planning_area_id,
                    routeId: problem.routeId,
                    version: problem.routeVersion,
                  }}
                  {...props}
                />
              )}
              size="xs"
              variant="light"
              leftSection={<TbMap2 size={14} />}
              onClick={event => event.stopPropagation()}
            >
              {t('openInPlanner')}
            </Button>
          )}
        </Table.Td>
      </Table.Tr>
      {isExpanded && (
        <Table.Tr>
          <Table.Td />
          <Table.Td colSpan={columnCount - 1}>
            <RouteProblemDetails problem={problem} types={detailTypes} />
          </Table.Td>
        </Table.Tr>
      )}
    </>
  )
}

export default memo(RouteProblemRow)
